import React, { useState, useEffect, useRef, useMemo } from 'react';
import { FaChevronDown, FaCrosshairs } from 'react-icons/fa';


import PropertyCard from './PropertyCard.jsx';
import { StyleConstants } from './StyleConstants';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import './mapbox-popup-fix.css';
import './map-cursor-mix.css';
import './styles/map-popups.css';
import './styles.css'; // Import main styles for Montserrat font
import { createPopupHtml } from './utils/mapPopupUtils';
import PhotoModal from './components/PhotoModal';
import Footer from './components/Footer/Footer';

// Set Mapbox token
mapboxgl.accessToken = import.meta.env.VITE_MAPBOX_TOKEN;

// Hide popup close button if not already done
const hidePopupCloseButton = () => {
  if (!document.querySelector('#mapbox-popup-fix')) {
    const style = document.createElement('style');
    style.id = 'mapbox-popup-fix';
    style.innerHTML = `.mapboxgl-popup-close-button { display: none !important; }`;
    document.head.appendChild(style);
  }
};
hidePopupCloseButton();

// Custom hook to manage photo modal state
function usePhotoModalState() {
  const [activeModalListing, setActiveModalListing] = useState(null);
  
  const showModal = (listing) => {
    setActiveModalListing(listing);
  };
  
  const hideModal = () => {
    setActiveModalListing(null);
  };
  
  return {
    activeModalListing,
    showModal,
    hideModal
  };
}

// Status filter options
const STATUS_FILTERS = [
  { label: 'All Recent', value: 'all' },
  { label: 'Active', value: 'Active' },
  { label: 'Pending', value: 'Pending' },
  { label: 'Closed', value: 'Closed' }
];

// Custom dropdown component
const CustomDropdown = ({ value, options, onChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const selectedOption = options.find(opt => opt.value === value) || options[0];

  return (
    <div ref={dropdownRef} style={{ position: 'relative', display: 'inline-block' }}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '8px 16px 8px 12px',
          borderRadius: '8px',
          border: '1px solid #ccc',
          background: '#fff',
          color: '#111',
          fontWeight: 600,
          fontSize: '16px',
          cursor: 'pointer',
          height: '40px',
          minWidth: '140px',
          textAlign: 'left'
        }}
      >
        <span>{selectedOption.label}</span>
        <FaChevronDown style={{ marginLeft: '12px', fontSize: '14px', color: '#666' }} />
      </button>
      
      {isOpen && (
        <div style={{
          position: 'absolute',
          top: '100%',
          left: 0,
          right: 0,
          backgroundColor: '#fff',
          border: '1px solid #ccc',
          borderRadius: '8px',
          marginTop: '4px',
          boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
          zIndex: 1000,
          overflow: 'hidden'
        }}>
          {options.map(option => (
            <div
              key={option.value}
              onClick={() => {
                onChange({ target: { value: option.value } });
                setIsOpen(false);
              }}
              style={{
                padding: '8px 16px',
                cursor: 'pointer',
                backgroundColor: option.value === value ? '#f5f5f5' : 'transparent',
                ':hover': {
                  backgroundColor: '#f5f5f5'
                }
              }}
            >
              {option.label}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Error Boundary Component
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    console.error('Listing display error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return <div style={{ padding: '20px', textAlign: 'center', color: '#c00' }}>
        Something went wrong with the listings display. Please try refreshing the page.
      </div>;
    }
    return this.props.children;
  }
}

export default function SplitView({ listings, isMobile }) {
  const galleryListRef = useRef(null);
  const [filteredListings, setFilteredListings] = useState([]);
  const [filter, setFilter] = useState('all');
  const DEFAULT_FILTER = 'all';
  const mapRef = useRef(null);
  const markersRef = useRef([]);
  const popupRef = useRef(null);
  const { activeModalListing, showModal, hideModal } = usePhotoModalState();


  // Filter listings when filter or listings prop changes
  useEffect(() => {
    if (!Array.isArray(listings)) { // Check if listings is an array
      setFilteredListings([]); // Default to empty if not an array (e.g., undefined during load)
      return;
    }
    
    if (filter === 'all') {
      setFilteredListings(listings);
    } else {
      setFilteredListings(listings.filter(listing => listing.status === filter));
    }
  }, [filter, listings]);

  // Memoized sorted listings
  const sortedListings = useMemo(() => {
    console.log('Recalculating sorted listings...');
    
    // Status priority mapping for sorting (moved inside useMemo)
    const STATUS_PRIORITY = {
      'Active': 1,
      'Coming Soon': 2,
      'Pending': 3,
      'Closed': 4
    };
    
    return [...filteredListings].sort((a, b) => {
      // First sort by status priority
      const statusA = STATUS_PRIORITY[a.status] || 999;
      const statusB = STATUS_PRIORITY[b.status] || 999;
      
      if (statusA !== statusB) {
        return statusA - statusB;
      }
      
      // Then sort by price (higher first)
      const priceA = parseFloat(a.price) || 0;
      const priceB = parseFloat(b.price) || 0;
      return priceB - priceA;
    });
  }, [filteredListings]);

  // Debug logging for first 3 sorted listings
  useEffect(() => {
    if (sortedListings.length > 0) {
      console.log('DEBUG: First 3 sorted listings:', 
        sortedListings.slice(0, 3).map(l => ({
          id: l.id,
          status: l.status,
          price: l.price
        }))
      );
    }
  }, [sortedListings]);

  // Initialize map (only once)
  useEffect(() => {
    const mapContainer = document.getElementById('split-view-map');
    if (!mapContainer) return;

    const map = new mapboxgl.Map({
      container: mapContainer,
      style: 'mapbox://styles/mapbox/streets-v11',
      center: [-121.3153, 44.0582], // Default center
      zoom: 10, // Default zoom
      attributionControl: true // Use Mapbox default attribution
    });
    map.addControl(new mapboxgl.NavigationControl({ showCompass: false }), 'bottom-right');
    
    mapRef.current = map;

    // Close popups when map is clicked
    map.on('click', () => {
      if (popupRef.current) {
        popupRef.current.remove();
        popupRef.current = null;
      }
    });

    // Cleanup map instance on component unmount
    return () => {
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
    };
  }, []); // Empty dependency array ensures this runs only once

  // Global click handler to close popup if click is outside popup
  useEffect(() => {
    function handleDocumentClick(e) {
      if (popupRef.current && popupRef.current._content && !popupRef.current._content.contains(e.target)) {
        popupRef.current.remove();
        popupRef.current = null;
      }
    }
    document.addEventListener('mousedown', handleDocumentClick, true);
    return () => {
      document.removeEventListener('mousedown', handleDocumentClick, true);
    };
  }, []);

  // Update markers when filteredListings or showModal changes
  useEffect(() => {
    const map = mapRef.current; // Get map instance from ref

    // Ensure map is initialized and filteredListings is an array before proceeding
    if (!map || !Array.isArray(filteredListings)) {
        // If no map or filteredListings is not an array, clear existing markers if any
        if (map && markersRef.current.length > 0) {
            markersRef.current.forEach(marker => marker.remove());
            markersRef.current = [];
        }
        return; // Exit if map not ready or filteredListings is not an array
    }

    // Always clear existing markers before adding new ones or if filteredListings is empty
    markersRef.current.forEach(marker => marker.remove());
    markersRef.current = [];

    // If filteredListings is empty, no new markers to add, and bounds should not be fitted.
    if (filteredListings.length === 0) {
        // Optionally, reset map view here if desired, e.g., to a default extent
        // map.flyTo({ center: [-121.3153, 44.0582], zoom: 10 });
        return;
    }
    
    // Create markers for listings with valid coordinates
    filteredListings.forEach(listing => {
      const lat = parseFloat(listing.raw?.Latitude);
      const lon = parseFloat(listing.raw?.Longitude);
      if (isNaN(lat) || isNaN(lon)) return; // Skip if no valid coordinates

      let markerColor = '#003DA5'; // Default color
      if (listing.status === 'Pending') markerColor = '#FFC107';
      else if (listing.status === 'Closed') markerColor = '#666666';
      else if (listing.status === 'Active') markerColor = '#4CAF50';
      
      const marker = new mapboxgl.Marker({ color: markerColor, scale: 0.8 })
        .setLngLat([lon, lat]);
      
      marker.getElement().addEventListener('click', (e) => {
        e.stopPropagation(); // Prevent map click event when marker is clicked
        
        // Close any existing popup
        if (popupRef.current) {
          popupRef.current.remove();
        }
        
        // Create and show new popup
        const popup = new mapboxgl.Popup({ 
          offset: 25,
          closeOnClick: false, 
          closeButton: true, 
          maxWidth: '240px',
          className: 'custom-mapbox-popup split-view-popup', // For custom styling
          closeOnMove: false // Keep popup open if map moves slightly
        });
        
        popup.setLngLat([lon, lat])
          .setHTML(createPopupHtml(listing, showModal, 'split')); // Use utility for HTML content
          
        popup.on('close', () => { // Handle programmatic close or user close
          if (popupRef.current === popup) {
            popupRef.current = null;
          }
        });
        
        popup.addTo(map);
        popupRef.current = popup; // Store reference to current open popup

        // Scroll corresponding listing card into view
        const listingElement = document.getElementById(`listing-card-${listing.id}`);
        if (listingElement) {
          listingElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      });
      
      marker.addTo(map);
      markersRef.current.push(marker); // Add to tracked markers
    });

    // Fit map to bounds only if new markers were added
    if (markersRef.current.length > 0) {
      const bounds = new mapboxgl.LngLatBounds();
      markersRef.current.forEach(marker => { // Iterate over actual markers in markersRef
        bounds.extend(marker.getLngLat()); // Extend bounds with each marker's LngLat
      });
      map.fitBounds(bounds, { padding: 60, maxZoom: 13, duration: 1000 });
    }
  }, [filteredListings, showModal]); // Dependencies: re-run if listings or modal function changes

  const handleRecenterMap = () => {
    const map = mapRef.current;
    if (!map || !filteredListings || filteredListings.length === 0) {
      console.log('Recenter: No map or no filtered listings to recenter.');
      return;
    }

    // Close any open popup before recentering
    if (popupRef.current) {
      popupRef.current.remove();
      popupRef.current = null;
    }

    const bounds = new mapboxgl.LngLatBounds();
    let validPointsExist = false;
    filteredListings.forEach(listing => {
      const lat = parseFloat(listing.raw?.Latitude);
      const lon = parseFloat(listing.raw?.Longitude);
      if (!isNaN(lat) && !isNaN(lon)) {
        bounds.extend([lon, lat]);
        validPointsExist = true;
      }
    });

    if (validPointsExist) {
      const padding = { top: 50, bottom: 50, left: 60, right: 60 };
      map.fitBounds(bounds, { 
        padding: padding,
        maxZoom: 13, 
        duration: 1000 
      });
    } else {
      console.log('Recenter: No valid points found in filtered listings.');
    }

    // Reset filter to default if not already
    if (filter !== 'all') setFilter('all');

    // Scroll gallery list to top
    if (galleryListRef.current) {
      galleryListRef.current.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };


  return (
    <>
      <div style={{ 
        display: 'flex', 
        height: 'calc(100vh - 110px)', // Fine-tuned to perfectly accommodate attribution
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* Left side - Listings */}
        <div style={{
          width: '50%',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          borderRight: '1px solid #e0e0e0',
          position: 'relative',
          overflow: 'hidden'
        }}>
        <div
          ref={galleryListRef}
          style={{
            flexGrow: 1,
            overflowY: 'auto',
            WebkitOverflowScrolling: 'touch',
            overscrollBehavior: 'none',
            padding: `${StyleConstants.contentTopPadding} ${StyleConstants.contentSidePadding} ${StyleConstants.contentBottomPadding}`,
          }}
        >
        {/* Filter controls */}
        <div style={{
          display: 'flex',
          alignItems: 'center', /* Aligned with Gallery View */
          justifyContent: 'space-between',
          marginBottom: '20px', /* Reduced to match Gallery View's spacing */
          padding: '0'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center'
          }}>
            <h2 style={{
              ...StyleConstants.sectionTitle,
              margin: 0,
              marginTop: 0,
              marginBottom: 0,
              padding: 0,
              lineHeight: '32px', /* Fixed pixel value to match Gallery view */
              fontSize: '24px',
              fontWeight: '700'
            }}>
              My Listings
              <span style={StyleConstants.statusCounter}>
                ({filteredListings.length})
              </span>
            </h2>
          </div>

          <CustomDropdown
            value={filter}
            options={STATUS_FILTERS}
            onChange={(e) => setFilter(e.target.value)}
          />
        </div>
        
        {/* Listings grid */}
          <ErrorBoundary>
            <div style={{
              ...StyleConstants.splitViewGrid,
              marginTop: '8px' /* Standard spacing */
            }}>
              {sortedListings?.length > 0 ? (
                sortedListings.map(listing => (
                  <div 
                    key={listing.id}
                    id={`listing-card-${listing.id}`}
                    style={{ cursor: 'pointer' }}
                  >
                    <PropertyCard 
  listing={listing} 
  isMobile={isMobile}
  onOpenModal={() => {
    if (popupRef.current) {
      popupRef.current.remove();
      popupRef.current = null;
    }
  }}
/>
                  </div>
                ))
              ) : (
                <div style={{ padding: '40px', textAlign: 'center', color: '#666' }}>
                  No listings found matching the selected filter.
                </div>
              )}
            </div>
          </ErrorBoundary>
        </div>
        {!isMobile && <Footer />}
      </div>

        {/* Right side - Map */}
        <div style={{ 
          width: '50%',
          height: '100%',
          position: 'relative',
          overflow: 'hidden'
        }}>
          <div 
            id="split-view-map"
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              margin: 0,
              padding: 0,
              overflow: 'hidden'
            }}
          />
          <button
            onClick={e => { handleRecenterMap(); if (e.currentTarget) e.currentTarget.blur(); }}
            style={{
              position: 'absolute',
              top: '10px',
              right: '10px',
              zIndex: 2, // Ensure it's above map tiles, potentially navigation controls if they were top-right
              background: 'white',
              border: '1px solid #dbdbdb',
              borderRadius: '4px',
              padding: '8px',
              cursor: 'pointer',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              lineHeight: '1'
            }}
            title="Recenter Map"
          >
            <FaCrosshairs size={18} color="#333" />
          </button>
        </div>
      </div>
      
      {/* Photo Modal - same component used in PropertyCard and MapView */}
      {activeModalListing && (
        <PhotoModal
          listing={activeModalListing}
          onClose={hideModal}
          isMobile={isMobile}
        />
      )}
    </>
  );
}
